import { useEffect, useState } from 'react';
import { Button, Container } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconArrowLeft,
  IconCalendar,
  IconCheck,
  IconClock,
  IconHeart,
  IconHome,
  IconMapPin,
  IconUsers,
} from '@tabler/icons-react';
import { ProperAssignedVisit } from './models/dashboardModels';
import { VISIT_STATUS } from '../../constants/visitsConstants';
import {
  getBadgeColorVisitStatusAdapter,
  getVisitStatusAdapter,
} from '../properties/adapters/visitsAdapter';
import useProperRentVisitActions from './hooks/useProperRentVisitActions';
import { Field, Section } from './components/VisitDetailParts';
import IntentSignalsCell from './components/IntentSignalsCell';
import InvestorFavoritesModal from './components/InvestorFavoritesModal';
import VisitFeedbackModal from './components/VisitFeedbackModal';
import VisitFeedbackCard from './components/VisitFeedbackCard';
import useVisitFeedback from './hooks/useVisitFeedback';
import { RentVisitFeedbackBody } from './services/dashboardService';
import { LeadMarketplaceType } from '../../components/VisitMarketplaceTypeBadge';
import ProperCancelPanel from './components/ProperCancelPanel';
import VisitMarketplaceTypeBadge from '../../components/VisitMarketplaceTypeBadge';

interface LocationState {
  visit?: ProperAssignedVisit;
}

const OK_STATUSES = [200, 201, 204];

const STEPS = [
  { key: VISIT_STATUS.PENDING_APPROVAL, label: 'Pendiente de aprobación' },
  { key: VISIT_STATUS.SCHEDULED, label: 'Programada' },
  { key: VISIT_STATUS.DONE, label: 'Finalizada' },
];

const getCurrentStep = (status: string): number => {
  switch (status) {
    case VISIT_STATUS.PENDING_APPROVAL:
      return 0;
    case VISIT_STATUS.SCHEDULED:
    case VISIT_STATUS.RESCHEDULED:
      return 1;
    case VISIT_STATUS.DONE:
      return 2;
    default:
      return -1;
  }
};

// Detalle de una visita de Renta/Secondary para el rol Proper. Sólo usa los
// endpoints que el Proper ya utiliza en su dashboard: marcar como realizada y
// cancelar.
const ProperRentVisitDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const visit = (location.state as LocationState | null)?.visit;

  const {
    cancellationReasons,
    doMarkAsDone,
    doSendFeedback,
    doCancelVisit,
    doLoadCancellationReasons,
  } = useProperRentVisitActions();

  const savedFeedback = useVisitFeedback(
    visit?.id,
    visit?.visitInfo.status === VISIT_STATUS.DONE &&
      visit?.visitInfo.has_feedback !== false,
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [error, setError] = useState('');
  const [favoritesOpen, setFavoritesOpen] = useState(false);

  useEffect(() => {
    if (visit) doLoadCancellationReasons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit?.id]);

  const goBack = () => navigate(-1);

  if (!visit) {
    return (
      <Container className="mt-6 visit-detail-v2" fluid>
        <Button className="vd-back" onClick={goBack}>
          <IconArrowLeft size={16} /> Volver a visitas
        </Button>
        <section className="vd-card">
          <p className="mb-0 text-muted">
            No se encontró la visita. Ábrela desde la lista del dashboard.
          </p>
        </section>
      </Container>
    );
  }

  const { visitInfo, propertyInfo } = visit;
  const status = visitInfo.status;
  const currentStep = getCurrentStep(status);
  const canMarkDone = [VISIT_STATUS.SCHEDULED, VISIT_STATUS.RESCHEDULED].includes(
    status as VISIT_STATUS,
  );

  const isSale = visitInfo.marketplace_type === LeadMarketplaceType.ON_SALE;
  const isDone = status === VISIT_STATUS.DONE;

  // El feedback se envía antes de cerrar la visita (el POST sólo acepta
  // visitas programadas o reprogramadas).
  const handleDone = () => {
    setError('');
    setFeedbackError('');
    setCancelOpen(false);
    setFeedbackOpen(true);
  };

  const submitFeedback = async (feedback: RentVisitFeedbackBody) => {
    setFeedbackSaving(true);
    setFeedbackError('');
    try {
      const sent = await doSendFeedback(visit.id, feedback);
      if (!sent.ok) {
        setFeedbackError(sent.message || 'No se pudo guardar el feedback.');
        return;
      }
      const result = await doMarkAsDone(visit.id);
      if (OK_STATUSES.includes(result)) goBack();
      else
        setFeedbackError(
          `Feedback guardado, pero (${result}) no fue posible marcar la visita como realizada. Intenta de nuevo.`,
        );
    } finally {
      setFeedbackSaving(false);
    }
  };

  const handleCancel = async (reason: string, explain: string) => {
    setError('');
    const result = await doCancelVisit(visit.id, reason, explain);
    if (OK_STATUSES.includes(result)) goBack();
    else setError(`(${result}) No fue posible cancelar la visita`);
  };

  return (
    <Container className="mt-6 visit-detail-v2" fluid>
      <Button className="vd-back" onClick={goBack}>
        <IconArrowLeft size={16} /> Volver a visitas
      </Button>

      <section className="vd-card vd-hero">
        <div className="vd-hero__top">
          <span className="vd-icon vd-icon--green vd-icon--lg">
            <IconHome size={26} />
          </span>
          <div>
            <div className="vd-hero__title-row">
              <h1 className="vd-hero__title">{propertyInfo.name}</h1>
              <span
                className={`vd-pill vd-pill--${getBadgeColorVisitStatusAdapter(
                  status,
                )}`}
              >
                {getVisitStatusAdapter(status)}
              </span>
            </div>
            <div className="vd-hero__sub">
              <IconMapPin size={14} /> {propertyInfo.address}
            </div>
            <div className="vd-hero__code">
              [{propertyInfo.code}] · {visit.id}
            </div>
          </div>
        </div>

        {canMarkDone && (
          <div className="vd-actions">
            <button
              type="button"
              className="vd-btn vd-btn--primary"
              onClick={handleDone}
            >
              <IconCheck size={16} /> Marcar como realizada
            </button>
            <button
              type="button"
              className="vd-btn vd-btn--danger"
              onClick={() => setCancelOpen((open) => !open)}
              aria-expanded={cancelOpen}
            >
              Cancelar
            </button>
          </div>
        )}

        {error && !cancelOpen && <p className="vd-panel__error">{error}</p>}

        {canMarkDone && cancelOpen && (
          <ProperCancelPanel
            title="Cancelar visita"
            reasons={cancellationReasons}
            error={error}
            onConfirm={handleCancel}
            onDiscard={() => {
              setCancelOpen(false);
              setError('');
            }}
          />
        )}
      </section>

      <div className="vd-layout">
        <div className="vd-col">
          <Section icon={<IconUsers size={20} />} tone="blue" title="Inquilino">
            <div className="vd-grid">
              <Field label="Nombre">{visitInfo.tenant_name}</Field>
              <Field label="Correo">{visitInfo.tenant_email}</Field>
              <Field label="Teléfono">{visitInfo.tenant_phone}</Field>
              <Field label="Negocio">
                <VisitMarketplaceTypeBadge
                  marketplace_type={visitInfo.marketplace_type}
                />
              </Field>
            </div>
          </Section>

          <Section icon={<IconCalendar size={20} />} tone="green" title="Visita">
            <div className="vd-grid">
              <Field label="Fecha">{visitInfo.visit_date}</Field>
              <Field label="Hora">{visitInfo.visit_time}</Field>
              <Field label="Precio">{propertyInfo.price}</Field>
            </div>
            <div className="vd-signals">
              <span className="vd-field__label">Señales de intención</span>
              <IntentSignalsCell
                favorites={visitInfo.tenant_favorites}
                visitDate={visitInfo.visit_date}
                visitTime={visitInfo.visit_time}
                status={status}
                onViewFavorites={() => setFavoritesOpen(true)}
              />
              {(visitInfo.tenant_favorites?.length ?? 0) > 0 && (
                <button
                  type="button"
                  className="vd-link"
                  onClick={() => setFavoritesOpen(true)}
                >
                  <IconHeart size={14} /> Ver favoritos
                </button>
              )}
            </div>
          </Section>
        </div>

        <div className="vd-col">
          <Section
            icon={<IconClock size={20} />}
            tone="orange"
            title="Estado de la visita"
          >
            {currentStep === -1 ? (
              <p className="mb-0">
                Esta visita está{' '}
                <strong>{getVisitStatusAdapter(status).toLowerCase()}</strong>.
              </p>
            ) : (
              <ol
                className={`vd-steps vd-steps--${getBadgeColorVisitStatusAdapter(
                  status,
                )}`}
              >
                {STEPS.map((step, index) => (
                  <li
                    key={step.key}
                    className={`vd-step${
                      index < currentStep ? ' is-done' : ''
                    }${index === currentStep ? ' is-current' : ''}`}
                  >
                    <span className="vd-step__dot">
                      {index < currentStep ? <IconCheck size={12} /> : index + 1}
                    </span>
                    <span className="vd-step__text">
                      <span className="vd-step__label">{step.label}</span>
                      {index === currentStep && (
                        <span className="vd-step__current">Estado actual</span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          {isDone && (
            <VisitFeedbackCard
              state={
                visitInfo.has_feedback === false
                  ? 'pending'
                  : savedFeedback.state
              }
              feedback={savedFeedback.feedback}
              isSale={isSale}
            />
          )}
        </div>
      </div>

      <VisitFeedbackModal
        isOpen={feedbackOpen}
        kind={isSale ? 'sale' : 'rent'}
        propertyName={propertyInfo.name}
        error={feedbackError}
        loading={feedbackSaving}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={submitFeedback}
      />
      <InvestorFavoritesModal
        isOpen={favoritesOpen}
        toggle={() => setFavoritesOpen(false)}
        investorName={visitInfo.tenant_name}
        favorites={visitInfo.tenant_favorites || []}
        currentListingId={propertyInfo.id}
      />
    </Container>
  );
};

export default ProperRentVisitDetail;
